//src/components/StocksOutlet.jsx
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { Outlet } from "react-router-dom";
import StocksNavbar from "./StocksNavbar";
import StocksSidebar from "./StocksSidebar";
import Heatmap from "./Heatmap";
import { getPort } from "../utils/getPort";

/* Phase 2B — URL path → market value.
   Each stock-market URL in App.js maps to the internal market key the
   Python backends and getPort.js use. Edit this table whenever a route is
   added or renamed in App.js. */
const PATH_TO_MARKET = {
  "/stocks/all_stock_markets": "dj", // landing page defaults to Dow Jones
  "/stocks/tadawul": "tadawul",
  "/stocks/nasdaq": "nasdaq",
  "/stocks/dj": "dj",
  "/stocks/sp500": "us",
  "/stocks/psx": "psx",
  "/stocks/nse": "nse",
  "/stocks/egx": "egx",
  "/stocks/qse": "qse",
  "/stocks/dfm": "dfm",
};

const LayoutContainer = styled.div`
  padding-top: 60px;

  @media only screen and (max-width: 768px) {
    padding-top: 40px;
  }
`;

const BodyWrapper = styled.div`
  display: flex;
`;

const MainContent = styled.main`
  padding-top: 60px; /* navbar height */
  margin-left: 220px;
  padding: 2rem;
  width: 100%;
  animation: fadeIn 0.4s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media only screen and (max-width: 768px) {
    margin-left: 0;
    padding: 1rem;
    padding-top: 60px;
  }
`;

/* Floating teal "☰ Menu" pill — mobile only, sits below the ticker + tabs */
const SidebarToggle = styled.button`
  display: none;

  @media only screen and (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 6px;
    position: fixed;
    top: 110px; /* below ticker (40-80) and AntD tabs (60-100) */
    left: 10px;
    padding: 8px 16px;
    background: teal;
    color: white;
    border: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    z-index: 10000; /* above AntD tabs (9999) and ticker (9998) */
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  }
`;

const Backdrop = styled.div`
  display: none;

  @media only screen and (max-width: 768px) {
    display: ${(props) => (props.open ? "block" : "none")};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1400;
  }
`;

export default function StocksOutlet() {
  const location = useLocation();

  // Phase 2B — market comes from the URL. For stock-market routes we look it
  // up in PATH_TO_MARKET; for Crypto/Metals/ETF we leave the initial value
  // alone (those child pages manage their own market state internally).
  const initialMarket = PATH_TO_MARKET[location.pathname] || "dj";
  const [market, setMarket] = React.useState(initialMarket);
  const [daysBack, setDaysBack] = React.useState(160);

  // Kept for backward-compat with child pages that still read these from
  // context (TadawulPage etc.). They are no longer displayed anywhere.
  const [volatility, setVolatility] = React.useState();
  const [zeroOCR, setZeroOCR] = React.useState();

  const [selectedSymbol, setSelectedSymbol] = React.useState(null);

  const [showDividends, setShowDividends] = React.useState(false);
  const [dividendsData, setDividendsData] = React.useState([]);

  const [showPERatio, setShowPERatio] = React.useState(false);
  const [PERatiodata, setPERatioData] = React.useState([]);

  const [showHeatmap, setShowHeatmap] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [rows, setRows] = React.useState([]);
  const [dailyRows, setDailyRows] = React.useState([]);
  const [hour3Rows, setHour3Rows] = React.useState([]);
  const [hour2Rows, setHour2Rows] = React.useState([]);
  const [hourlyRows, setHourlyRows] = React.useState([]);
  const [weeklyRows, setWeeklyRows] = React.useState([]);

  const [divPos, setDivPos] = React.useState({
    x: window.innerWidth - 420,
    y: 100,
  });
  const [dragging, setDragging] = React.useState(false);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });

  const [pePos, setPePos] = React.useState({ x: 40, y: 120 });
  const [draggingPE, setDraggingPE] = React.useState(false);
  const [offsetPE, setOffsetPE] = React.useState({ x: 0, y: 0 });

  const startDrag = (e) => {
    setDragging(true);
    setOffset({ x: e.clientX - divPos.x, y: e.clientY - divPos.y });
  };

  const onDrag = (e) => {
    if (!dragging) return;
    const newX = e.clientX - offset.x;
    const newY = e.clientY - offset.y;
    const boundedY = Math.max(60, newY);
    setDivPos({ x: newX, y: boundedY });
  };

  const stopDrag = () => setDragging(false);

  const startDragPE = (e) => {
    setDraggingPE(true);
    setOffsetPE({ x: e.clientX - pePos.x, y: e.clientY - pePos.y });
  };

  const onDragPE = (e) => {
    if (!draggingPE) return;
    const newX = e.clientX - offsetPE.x;
    const newY = e.clientY - offsetPE.y;
    const boundedY = Math.max(60, newY);
    setPePos({ x: newX, y: boundedY });
  };

  const stopDragPE = () => setDraggingPE(false);

  const openHeatmap = () => {
    if (!selectedSymbol) return;
    setShowHeatmap(true);
  };

  React.useEffect(() => {
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("mousemove", onDragPE);
    window.addEventListener("mouseup", stopDragPE);

    return () => {
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("mousemove", onDragPE);
      window.removeEventListener("mouseup", stopDragPE);
    };
  });

  // Phase 2B — whenever the URL changes to a stock-market route, pull the
  // matching market out of PATH_TO_MARKET and update state. Non-stock pages
  // (crypto/metals/etf) are skipped so they keep running on their own market.
  React.useEffect(() => {
    const m = PATH_TO_MARKET[location.pathname];
    if (m) setMarket(m);
  }, [location.pathname]);

  const openDividends = async () => {
    if (!selectedSymbol) return;
    const res = await fetch(
      `${getPort(market)}/api/dividends/${selectedSymbol}`
    );
    const data = await res.json();
    setDividendsData(data);
    setShowDividends(true);
  };

  const openPERatio = async () => {
    if (!selectedSymbol) return;
    const res = await fetch(`${getPort(market)}/api/pe/${selectedSymbol}`);
    const data = await res.json();
    setPERatioData(data);
    setShowPERatio(true);
  };

  return (
    <LayoutContainer>
      <StocksNavbar
        market={market}
        setMarket={setMarket}
        daysBack={daysBack}
        setDaysBack={setDaysBack}
        hideMarket={
          location.pathname === "/stocks/crypto" ||
          location.pathname === "/stocks/metals" ||
          location.pathname === "/stocks/etf"
        }
      />

      <SidebarToggle onClick={() => setSidebarOpen(true)}>
        ☰ Menu
      </SidebarToggle>
      <Backdrop open={sidebarOpen} onClick={() => setSidebarOpen(false)} />

      <BodyWrapper>
        <StocksSidebar
          openDividends={openDividends}
          openPERatio={openPERatio}
          openHeatmap={openHeatmap}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <MainContent>
          <Outlet
            context={{
              market,
              daysBack,
              volatility,
              setVolatility,
              zeroOCR,
              setZeroOCR,
              selectedSymbol,
              setSelectedSymbol,
              rows,
              setRows,
              dailyRows,
              setDailyRows,
              hour3Rows,
              setHour3Rows,
              hour2Rows,
              setHour2Rows,
              hourlyRows,
              setHourlyRows,
              weeklyRows,
              setWeeklyRows,
            }}
          />

          {showDividends && (
            <div
              style={{
                position: "fixed",
                top: divPos.y,
                left: divPos.x,
                width: "350px",
                background: "white",
                border: "2px solid #ccc",
                borderRadius: "12px",
                padding: "0",
                zIndex: 2000,
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                userSelect: "none",
              }}
            >
              <div
                onMouseDown={startDrag}
                style={{
                  padding: "10px",
                  background: "#f0f0f0",
                  borderBottom: "1px solid #ccc",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                <strong>Dividends — {selectedSymbol}</strong>
                <span
                  onClick={() => setShowDividends(false)}
                  style={{
                    cursor: "pointer",
                    fontWeight: "bold",
                    padding: "4px 8px",
                    borderRadius: "6px",
                    background: "#ddd",
                  }}
                >
                  ✕
                </span>
              </div>

              <div
                style={{
                  padding: "20px",
                  maxHeight: "400px",
                  overflowY: "auto",
                }}
              >
                {dividendsData.dividends &&
                  dividendsData.dividends.length === 0 && (
                    <p>No dividends found.</p>
                  )}

                {dividendsData.dividends &&
                  dividendsData.dividends.length > 0 && (
                    <>
                      <p>
                        <strong>Frequency:</strong> {dividendsData.frequency}
                      </p>
                      <p>
                        <strong>Last Dividend:</strong>{" "}
                        {dividendsData.last_dividend}
                      </p>
                      <p>
                        <strong>Next Expected:</strong>{" "}
                        {dividendsData.next_expected}
                      </p>
                      <p>
                        <strong>Next Ex‑Dividend Date:</strong>{" "}
                        {dividendsData.next_ex_date}
                      </p>

                      <h4>Dividend History</h4>
                      <ul>
                        {dividendsData.dividends.map((d, i) => (
                          <li key={i}>
                            {dividendsData.years[i]} — {d}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDividends(false);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {showPERatio && (
            <div
              style={{
                position: "fixed",
                top: pePos.y,
                left: pePos.x,
                width: "300px",
                background: "white",
                border: "2px solid #ccc",
                borderRadius: "12px",
                padding: "0",
                zIndex: 2000,
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                userSelect: "none",
              }}
            >
              <div
                onMouseDown={startDragPE}
                style={{
                  padding: "10px",
                  background: "#ffe6cc",
                  borderBottom: "1px solid #ccc",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                <strong>P/E Ratio — {selectedSymbol}</strong>
                <span
                  onClick={() => setShowPERatio(false)}
                  style={{
                    cursor: "pointer",
                    fontWeight: "bold",
                    padding: "4px 8px",
                    borderRadius: "6px",
                    background: "#ddd",
                  }}
                >
                  ✕
                </span>
              </div>

              <div
                style={{
                  padding: "20px",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {PERatiodata.pe_ratio === null && (
                  <p>No P/E ratio available.</p>
                )}

                {PERatiodata.pe_ratio !== null && (
                  <>
                    <p>
                      <strong>Price:</strong> {PERatiodata.price}
                    </p>
                    <p>
                      <strong>EPS (TTM):</strong> {PERatiodata.eps_ttm}
                    </p>
                    <p>
                      <strong>P/E Ratio:</strong> {PERatiodata.pe_ratio}
                    </p>
                    <p>
                      <strong>Currency:</strong> {PERatiodata.currency}
                    </p>
                  </>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPERatio(false);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {showHeatmap && (
            <Heatmap
              symbol={selectedSymbol}
              market={market}
              dailyRows={dailyRows}
              hour3Rows={hour3Rows}
              hour2Rows={hour2Rows}
              hourlyRows={hourlyRows}
              weeklyRows={weeklyRows}
              onClose={() => setShowHeatmap(false)}
            />
          )}
        </MainContent>
      </BodyWrapper>
    </LayoutContainer>
  );
}
